import { DecimalPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { CompanyObjectivePeriodStatus } from 'src/app/enums/company-objective-period-status.enum';
import { ActiveCompanyObjectiveKeyResult } from 'src/app/models/active-company-objective.model';
import { PermissionsService } from 'src/app/services/permissions.service';
import {
  CompanyMetricVariableConditionFormDialogComponent,
  CompanyMetricVariableConditionFormDialogData,
} from 'src/app/components/main/shared/company-products/company-product-detail/company-product-objectives/company-metric-variable-condition-form-dialog.component';
import {
  CompanyObjectiveKeyResultIngestHelpDialogComponent,
  CompanyObjectiveKeyResultIngestHelpDialogData,
} from './company-objective-key-result-ingest-help-dialog.component';
import { CompanyMetricValueVisualizationDialogComponent } from './company-metric-value-visualization-dialog/company-metric-value-visualization-dialog.component';

@Component({
  selector: 'app-company-objective-key-result',
  templateUrl: './company-objective-key-result.component.html',
  styleUrl: './company-objective-key-result.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [DecimalPipe, MatButtonModule, MatIconModule, MatMenuModule, MatTooltipModule],
})
export class CompanyObjectiveKeyResultComponent {
  private readonly dialog = inject(MatDialog);
  private readonly permissions = inject(PermissionsService);

  readonly key_result = input.required<ActiveCompanyObjectiveKeyResult>();
  readonly company_product_id = input<number | null>(null);
  readonly layout = input<'dashboard' | 'management'>('dashboard');
  readonly period_status = input<CompanyObjectivePeriodStatus>(CompanyObjectivePeriodStatus.Active);
  readonly timeline_progress = input<number | null>(null);
  readonly show_options = input(false);
  readonly can_update = input(false);
  readonly can_delete = input(false);

  readonly edit = output<void>();
  readonly delete = output<void>();
  readonly variable_condition_updated = output<void>();

  readonly vm = computed(() =>
    buildCompanyObjectiveKeyResultViewModel(this.key_result(), this.layout(), this.period_status(), this.timeline_progress())
  );

  readonly can_open_variable_condition_dialog = computed(() => {
    const company_product_id = this.company_product_id();
    if (company_product_id === null) {
      return false;
    }

    const is_product_manager = this.permissions.isBisualProductionTeamInCompanyProductWithRole(
      String(company_product_id),
      'product-manager'
    );
    if (!is_product_manager) {
      return false;
    }

    return this.key_result().company_metric_variable_condition_id > 0;
  });

  openVisualization(): void {
    const chart = this.vm().visualization_chart;
    if (!chart) {
      return;
    }

    this.dialog.open(CompanyMetricValueVisualizationDialogComponent, {
      data: {
        title: this.key_result().title,
        chart,
      },
      width: '760px',
    });
  }

  openIngestHelp(): void {
    const key_result = this.key_result();
    const company_product_id = this.company_product_id();
    if (company_product_id === null) {
      return;
    }

    const data: CompanyObjectiveKeyResultIngestHelpDialogData = {
      key_result_title: key_result.title,
      company_metric_slug: key_result.company_metric_slug,
      company_metric_title: key_result.company_metric_title,
      company_metric_relatable: key_result.company_metric_relatable,
      company_product_id,
      unit: key_result.unit,
    };

    this.dialog.open(CompanyObjectiveKeyResultIngestHelpDialogComponent, {
      width: '640px',
      maxWidth: '95vw',
      data,
    });
  }

  openVariableConditionDialog(): void {
    const key_result = this.key_result();
    const data: CompanyMetricVariableConditionFormDialogData = {
      company_metric_variable_condition_id: key_result.company_metric_variable_condition_id,
      key_result_title: key_result.title,
      acceptable_value: key_result.acceptable_value,
      good_value: key_result.good_value,
      evaluation: key_result.evaluation,
      unit: key_result.unit,
      can_edit_variable_condition: key_result.can_edit_variable_condition,
    };

    this.dialog
      .open(CompanyMetricVariableConditionFormDialogComponent, {
        width: '560px',
        maxWidth: '95vw',
        data,
      })
      .afterClosed()
      .subscribe((saved: boolean | undefined) => {
        if (saved) {
          this.variable_condition_updated.emit();
        }
      });
  }
}

export function buildCompanyObjectiveKeyResultViewModel(
  key_result: ActiveCompanyObjectiveKeyResult,
  layout: 'dashboard' | 'management',
  period_status: CompanyObjectivePeriodStatus,
  timeline_progress: number | null
): CompanyObjectiveKeyResultViewModel {
  const show_trend = layout === 'dashboard' || period_status === CompanyObjectivePeriodStatus.Active;
  const trend_percent =
    show_trend && key_result.current_value !== null && key_result.trend_value !== null && key_result.trend_value !== 0
      ? ((key_result.current_value - key_result.trend_value) / Math.abs(key_result.trend_value)) * 100
      : null;

  const trend_direction = trend_percent === null ? 'flat' : trend_percent > 0 ? 'up' : trend_percent < 0 ? 'down' : 'flat';

  const trend_semantic_class =
    trend_percent === null || key_result.evaluation === null || key_result.evaluation === 'exact'
      ? 'neutral'
      : key_result.evaluation === 'higher_is_better'
        ? trend_percent >= 0
          ? 'good'
          : 'bad'
        : trend_percent <= 0
          ? 'good'
          : 'bad';

  const trend_tooltip =
    key_result.trend_value === null
      ? 'Respecto hace 2 semanas: —'
      : `Respecto hace 2 semanas: ${key_result.trend_value.toFixed(2)}${key_result.unit ? ` ${key_result.unit}` : ''}`;

  const bar_min_candidate = key_result.worst_value ?? key_result.current_value ?? 0;
  const bar_max_candidate = Math.max(
    key_result.good_value ?? bar_min_candidate,
    key_result.acceptable_value ?? bar_min_candidate,
    key_result.current_value ?? bar_min_candidate,
    bar_min_candidate
  );
  const bar_max = bar_max_candidate === bar_min_candidate ? bar_max_candidate + 1 : bar_max_candidate;
  const to_percent = (value: number | null): number | null =>
    value === null ? null : ((value - bar_min_candidate) / (bar_max - bar_min_candidate)) * 100;

  const show_threshold_bar = layout === 'dashboard' || period_status !== CompanyObjectivePeriodStatus.Future;
  const stale_days = layout === 'management' ? 30 : 3;
  const is_stale_update =
    layout === 'management'
      ? key_result.latest_updated_at === null || isOlderThanDays(key_result.latest_updated_at, stale_days)
      : isOlderThanDays(key_result.latest_updated_at, stale_days);
  const show_no_metric_data = key_result.latest_updated_at === null;
  const show_last_updated =
    !show_no_metric_data &&
    (layout === 'dashboard' ||
      period_status === CompanyObjectivePeriodStatus.Active ||
      period_status === CompanyObjectivePeriodStatus.Past);
  const show_score_chip =
    !show_no_metric_data &&
    (layout === 'dashboard' || period_status !== CompanyObjectivePeriodStatus.Future || key_result.current_value !== null);
  const show_timeline = layout === 'management' && period_status === CompanyObjectivePeriodStatus.Active;
  const timeline_pct = timeline_progress === null ? 0 : timeline_progress * 100;

  return {
    ...key_result,
    trend_percent,
    trend_direction,
    trend_semantic_class,
    trend_tooltip,
    latest_updated_at_relative: formatRelativeTime(key_result.latest_updated_at),
    is_stale_update: period_status === CompanyObjectivePeriodStatus.Active && is_stale_update,
    visualization_chart: parseChartJson((key_result.visualization?.['chart_type_json'] as string | Record<string, unknown> | null) ?? null),
    bar_min: bar_min_candidate,
    bar_max,
    bar_current_pct: to_percent(key_result.current_value),
    bar_acceptable_pct: to_percent(key_result.acceptable_value),
    bar_good_pct: to_percent(key_result.good_value),
    current_state: metricStateFromThresholds(
      key_result.current_value,
      key_result.acceptable_value,
      key_result.good_value,
      key_result.evaluation
    ),
    score_state: scoreStateFromScoring(key_result.scoring),
    show_trend,
    show_threshold_bar,
    show_last_updated,
    show_score_chip,
    show_no_metric_data,
    show_timeline,
    timeline_pct,
    last_updated_label:
      period_status === CompanyObjectivePeriodStatus.Past
        ? formatAbsoluteDate(key_result.latest_updated_at)
        : formatRelativeTime(key_result.latest_updated_at),
  };
}

export type OkrMetricState = 'neutral' | 'low' | 'mid' | 'good';

export function scoreStateFromScoring(scoring: number | null): OkrMetricState {
  if (scoring === null) {
    return 'neutral';
  }
  if (scoring < 4) {
    return 'low';
  }
  if (scoring <= 7.5) {
    return 'mid';
  }
  return 'good';
}

export function metricStateFromThresholds(
  current_value: number | null,
  acceptable_value: number | null,
  good_value: number | null,
  evaluation: ActiveCompanyObjectiveKeyResult['evaluation']
): OkrMetricState {
  if (current_value === null || acceptable_value === null || good_value === null || evaluation === null) {
    return 'neutral';
  }

  if (evaluation === 'lower_is_better') {
    if (current_value > acceptable_value) {
      return 'low';
    }
    if (current_value > good_value) {
      return 'mid';
    }
    return 'good';
  }

  if (evaluation === 'exact') {
    const distance_to_good = Math.abs(current_value - good_value);
    const acceptable_distance = Math.abs(acceptable_value - good_value);
    if (acceptable_distance === 0) {
      return current_value === good_value ? 'good' : 'low';
    }
    if (distance_to_good > acceptable_distance) {
      return 'low';
    }
    if (distance_to_good > 0) {
      return 'mid';
    }
    return 'good';
  }

  if (current_value < acceptable_value) {
    return 'low';
  }
  if (current_value < good_value) {
    return 'mid';
  }
  return 'good';
}

function formatRelativeTime(iso_date: string | null): string {
  if (!iso_date) {
    return 'sin datos';
  }

  const date = new Date(iso_date);
  const diff_ms = Date.now() - date.getTime();
  const diff_hours = Math.floor(diff_ms / 3600000);
  if (diff_hours < 1) {
    return 'hace menos de 1 hora';
  }
  if (diff_hours < 24) {
    const hour_label = diff_hours === 1 ? 'hora' : 'horas';
    return `hace ${diff_hours} ${hour_label}`;
  }
  const diff_days = Math.floor(diff_hours / 24);
  const day_label = diff_days === 1 ? 'día' : 'días';
  return `hace ${diff_days} ${day_label}`;
}

function formatAbsoluteDate(iso_date: string | null): string {
  if (!iso_date) {
    return 'sin datos';
  }

  return new Date(iso_date).toLocaleDateString('es-ES');
}

function isOlderThanDays(iso_date: string | null, days: number): boolean {
  if (!iso_date) {
    return false;
  }

  const date = new Date(iso_date);
  const diff_ms = Date.now() - date.getTime();

  return diff_ms > days * 24 * 60 * 60 * 1000;
}

function parseChartJson(chart_type_json: string | Record<string, unknown> | null): Record<string, unknown> | null {
  if (!chart_type_json) {
    return null;
  }
  if (typeof chart_type_json === 'string') {
    try {
      return JSON.parse(chart_type_json) as Record<string, unknown>;
    } catch {
      return null;
    }
  }
  return chart_type_json;
}

export interface CompanyObjectiveKeyResultViewModel extends ActiveCompanyObjectiveKeyResult {
  trend_percent: number | null;
  trend_direction: 'up' | 'down' | 'flat';
  trend_semantic_class: 'good' | 'bad' | 'neutral';
  trend_tooltip: string;
  latest_updated_at_relative: string;
  is_stale_update: boolean;
  visualization_chart: Record<string, unknown> | null;
  bar_min: number;
  bar_max: number;
  bar_current_pct: number | null;
  bar_acceptable_pct: number | null;
  bar_good_pct: number | null;
  current_state: OkrMetricState;
  score_state: OkrMetricState;
  show_trend: boolean;
  show_threshold_bar: boolean;
  show_last_updated: boolean;
  show_score_chip: boolean;
  show_no_metric_data: boolean;
  show_timeline: boolean;
  timeline_pct: number;
  last_updated_label: string;
}
